import { Controller } from "@hotwired/stimulus"
import { readShareParams } from "../modules/salary_lab/url_state"
import { trackPdfRequested, trackPdfDownloaded } from "../modules/salary_lab/ga4_tracker"

const MAX_PDF_SALARY = 25_000_000

export default class extends Controller {
  static targets = ["button", "spinner", "statusText", "errorText"]
  static values = { calculatorType: String, reportVariant: String }

  async generate(event) {
    event.preventDefault()
    if (this._polling) return

    this._showLoading()
    this._clearError()

    try {
      const params = this._readParams()

      if (this._exceedsMaxSalary(params)) {
        this._showError("PDF reports are not available for values above $25,000,000.")
        return
      }
      trackPdfRequested(this.calculatorTypeValue, this.reportVariantValue)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch("/salary-lab/generate-pdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({
          calculator_type: this.calculatorTypeValue,
          report_variant: this.reportVariantValue,
          params,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 401 && data.sign_in_url) {
          window.location.href = data.sign_in_url
          return
        }
        this._showError(data.error || this._errorForStatus(response.status))
        return
      }

      if (data.status === "completed") {
        this._triggerDownload(data.download_url)
        this._hideLoading()
        return
      }

      if (data.status === "processing" && data.poll_url) {
        this._pollForCompletion(data.poll_url)
        return
      }

      this._showError("Unexpected response")
    } catch {
      this._showError("Network error. Please try again.")
    }
  }

  _pollForCompletion(pollUrl) {
    this._polling = true
    this._pollCount = 0
    const maxAttempts = 30

    this._pollInterval = setInterval(async () => {
      this._pollCount++
      if (this._pollCount > maxAttempts) {
        this._stopPolling()
        this._showError("Generation timed out. Please try again.")
        return
      }

      this._updateStatus(`Generating PDF... (${this._pollCount}s)`)

      try {
        const response = await fetch(pollUrl, {
          headers: { Accept: "application/json" },
        })
        const data = await response.json()

        if (data.status === "completed") {
          this._stopPolling()
          this._triggerDownload(data.download_url)
          this._hideLoading()
        } else if (data.status === "failed") {
          this._stopPolling()
          this._showError(data.error || "Generation failed. Please try again.")
        }
      } catch {
        this._stopPolling()
        this._showError("Connection lost. Please try again.")
      }
    }, 500)
  }

  _stopPolling() {
    this._polling = false
    if (this._pollInterval) {
      clearInterval(this._pollInterval)
      this._pollInterval = null
    }
  }

  _triggerDownload(url) {
    trackPdfDownloaded(this.calculatorTypeValue, this.reportVariantValue)
    window.location.href = url
  }

  _showLoading() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = true
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden")
    this._updateStatus("Preparing PDF...")
  }

  _hideLoading() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = false
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
    this._updateStatus("")
  }

  _updateStatus(text) {
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
  }

  _showError(message) {
    this._hideLoading()
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorTextTarget.classList.remove("hidden")
    }
  }

  _clearError() {
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = ""
      this.errorTextTarget.classList.add("hidden")
    }
  }

  _errorForStatus(status) {
    switch (status) {
      case 401:
        return "Please sign in to download PDF reports."
      case 403:
        return "This report is available with a Premium subscription. Upgrade to unlock full PDF reports."
      case 429:
        return "Monthly download limit reached."
      default:
        return "Something went wrong. Please try again."
    }
  }

  _exceedsMaxSalary(params) {
    const fields = ["gross_salary", "rate", "target_net", "current_salary", "take_home"]

    // Check top-level params
    for (const field of fields) {
      if (params[field] && parseFloat(params[field]) > MAX_PDF_SALARY) return true
    }

    // Check comparison scenarios
    for (const key of ["scenario_a", "scenario_b"]) {
      const scenario = params[key]
      if (!scenario) continue
      for (const field of fields) {
        if (scenario[field] && parseFloat(scenario[field]) > MAX_PDF_SALARY) return true
      }
    }

    return false
  }

  _readParams() {
    const el = document.querySelector("[data-current-params]")
    if (el) {
      try { return JSON.parse(el.dataset.currentParams) } catch { /* fall through */ }
    }
    return readShareParams(this.calculatorTypeValue)
  }

  disconnect() {
    this._stopPolling()
  }
}
